// @subframe/sync-disable

'use client';
/*
 * Documentation:
 * Radio Group — https://app.subframe.com/6fc7f5fbb098/library?component=Radio+Group_c4b6300e-20b4-4f3e-8b9f-379a046674ca
 */

import * as SubframeCore from '@subframe/core';
import React from 'react';
import * as SubframeUtils from '../utils';

interface OptionProps extends React.ComponentProps<typeof SubframeCore.RadioGroup.Item> {
  label?: React.ReactNode;
  disabled?: boolean;
  checked?: boolean;
  className?: string;
}

const Option = React.forwardRef<HTMLButtonElement, OptionProps>(function Option(
  { label, disabled = false, checked = false, className, ...otherProps }: OptionProps,
  ref,
) {
  return (
    <SubframeCore.RadioGroup.Item
      disabled={disabled}
      className={SubframeUtils.twClassNames(
        'group/0f804ad9 focus-visible:ring-brand-600/10 flex cursor-pointer items-center gap-2 rounded-sm border-none bg-transparent text-left transition-all focus:outline-none focus-visible:ring-4 disabled:cursor-default',
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex h-4 items-center gap-2">
        <div className="bg-default-background group-active/0f804ad9:border-brand-700 group-aria-[checked=true]/0f804ad9:border-brand-600 flex h-4 w-4 flex-none flex-col items-center justify-center gap-2 rounded-full border-2 border-solid border-neutral-300 transition-all group-active/0f804ad9:border-2 group-active/0f804ad9:border-solid group-disabled/0f804ad9:border-2 group-disabled/0f804ad9:border-solid group-disabled/0f804ad9:border-neutral-200 group-disabled/0f804ad9:bg-neutral-100 group-active/0f804ad9:group-disabled/0f804ad9:border-2 group-active/0f804ad9:group-disabled/0f804ad9:border-solid group-active/0f804ad9:group-disabled/0f804ad9:border-neutral-200 group-aria-[checked=true]/0f804ad9:border-2 group-aria-[checked=true]/0f804ad9:border-solid">
          <div className="bg-default-background group-aria-[checked=true]/0f804ad9:bg-brand-600 hidden h-2 w-2 flex-none flex-col items-start gap-2 rounded-full group-disabled/0f804ad9:bg-neutral-200 group-aria-[checked=true]/0f804ad9:flex" />
        </div>
      </div>
      {label ? (
        <span className="text-body font-body text-default-font group-disabled/0f804ad9:text-subtext-color">
          {label}
        </span>
      ) : null}
    </SubframeCore.RadioGroup.Item>
  );
});

interface RadioGroupRootProps extends React.ComponentProps<typeof SubframeCore.RadioGroup.Root> {
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  error?: boolean;
  horizontal?: boolean;
  children?: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

const RadioGroupRoot = React.forwardRef<HTMLDivElement, RadioGroupRootProps>(
  function RadioGroupRoot(
    {
      label,
      helpText,
      error = false,
      horizontal = false,
      children,
      className,
      value,
      onValueChange,
      ...otherProps
    }: RadioGroupRootProps,
    ref,
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          'group/c4b6300e flex flex-col items-start gap-2',
          className,
        )}
        ref={ref}
      >
        <SubframeCore.RadioGroup.Root value={value} onValueChange={onValueChange} {...otherProps}>
          {label ? (
            <span className="text-body-bold font-body-bold text-default-font">{label}</span>
          ) : null}
          {children ? (
            <div
              className={SubframeUtils.twClassNames('flex flex-col items-start gap-2', {
                'flex-row flex-nowrap gap-6': horizontal,
              })}
            >
              {children}
            </div>
          ) : null}
          {helpText ? (
            <span
              className={SubframeUtils.twClassNames(
                'text-caption font-caption text-subtext-color',
                { 'text-error-700': error },
              )}
            >
              {helpText}
            </span>
          ) : null}
        </SubframeCore.RadioGroup.Root>
      </div>
    );
  },
);

export const RadioGroup = Object.assign(RadioGroupRoot, {
  Option,
});
